import React, { useState } from 'react';
import { Modal, TextField } from '@gerdau/hefesto';
import {
  ContainerInput,
  ContainerLink,
  ExternalLink,
  EyeIcon,
  EyeOffIcon
} from './styles';
import { LoginService } from '../../../services/Login';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { addToast } from '../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../store/ducks/toast/types';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import { resetUserSession } from '../../../store/ducks/user/actions';
import { useLocation } from 'react-router-dom';
import { deleteCookie } from '../../../services/MVP/utils';
import { CookiesKey } from '../../../services/Auth/enums';

interface ModalLoginProps {
  close: () => void;
  exit: () => void;
  refreshToken: () => void;
}

const ModalLogin: React.FC<ModalLoginProps> = ({
  close,
  exit,
  refreshToken
}) => {
  const dispatch = useDispatch();
  const userEmail = useSelector((state: ApplicationState) => state.user?.email);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const loginService = new LoginService();

  const location = useLocation();

  const validateErrorEmail = () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !email.length || !emailPattern.test(email)) {
      dispatch(
        sendTagging({
          event: 'erro_login',
          screenName: location.pathname,
          ecommerce: {
            modal_name: 'expiracao_de_sessao'
          }
        })
      );
      setErrors({ ...errors, email: true });
    } else {
      setErrors({ ...errors, email: false });
    }
  };

  const validateErrorPassword = () => {
    if (!password || !password.length) {
      dispatch(
        sendTagging({
          event: 'erro_login',
          screenName: location.pathname,
          ecommerce: {
            modal_name: 'expiracao_de_sessao'
          }
        })
      );
      setErrors({ ...errors, password: true });
    } else {
      setErrors({ ...errors, password: false });
    }
  };

  const handleFinishSession = () => {
    dispatch(
      sendTagging({
        event: 'encerrar_sessao',
        screenName: location.pathname,
        ecommerce: {
          sessionStatus: 'sessao_expirada'
        }
      })
    );
    close();
    exit();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    validateErrorEmail();
    validateErrorPassword();

    if (errors.email || errors.password) {
      return;
    }

    dispatch(
      sendTagging({
        event: 'continuar_sessao',
        screenName: location.pathname,
        ecommerce: {
          sessionStatus: 'sessao_expirada'
        }
      })
    );
    await login();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === 'enter') {
      e.preventDefault();
      validateErrorEmail();
      validateErrorPassword();
      const formEvent = new Event('submit', { bubbles: true });
      const form = e.currentTarget.closest('form') as HTMLFormElement;
      form?.dispatchEvent(formEvent); // Dispara o evento de submit manualmente
    }
  };

  const login = async () => {
    setShowLoading(true);
    try {
      await refreshToken();
      const token = await loginService.login({
        username: email,
        password
      });
      if (!token || !token.accessToken) {
        throw new Error();
      }
      deleteCookie(CookiesKey.ACCESS_TOKEN);
      LoginService.setToken(token.accessToken);
      deleteCookie(CookiesKey.SSO_ACCESS);
      LoginService.setSsoAccess(false);
      // evita reutilizar o token de perfil da conta anterior nos headers
      deleteCookie(CookiesKey.PROFILE_ACCESS_TOKEN);
      // mesmo usuário reautenticando preserva a jornada (carrinho/cliente);
      // outro usuário faz o reset completo para não herdar dados da conta anterior
      const isSameUser =
        !!userEmail &&
        email.trim().toLowerCase() === userEmail.trim().toLowerCase();
      dispatch(resetUserSession({ keepSession: isSameUser }));
      setEmail('');
      setPassword('');
      setErrors({ email: false, password: false });
      close();
    } catch (error) {
      console.error('Error login', error);
      dispatch(
        addToast({
          message: 'E-mail ou senha incorretos',
          type: EToastTypes.DANGER,
          durationInMS: 5000,
          spaceToTop: 77
        })
      );
      throw error;
    } finally {
      setShowLoading(false);
    }
  };

  const loginForm = (
    <form onKeyUp={handleKeyDown} onSubmit={handleSubmit}>
      <ContainerInput error={errors.email}>
        <TextField
          label={'E-mail'}
          name={email}
          value={email}
          required
          onChange={(e) => {
            const regex = /[^a-zA-Z0-9@._-]/g;
            const formattedValue = e.target.value.replace(regex, '');
            setEmail(formattedValue);
          }}
          onBlur={validateErrorEmail}
          placeholder="Digite o seu e-mail"
          variant={errors.email ? 'danger' : undefined}
          supportMessage={errors.email ? 'E-mail inválido' : ''}
          maxLength={128}
        />
      </ContainerInput>

      <ContainerInput error={errors.password}>
        <TextField
          required
          label={'Senha'}
          name={password}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={validateErrorPassword}
          placeholder="Digite a sua senha"
          variant={errors.password ? 'danger' : undefined}
          supportMessage={errors.password ? 'Senha inválida' : ''}
          icon={
            showPassword ? (
              <EyeIcon onClick={() => setShowPassword(!showPassword)}></EyeIcon>
            ) : (
              <EyeOffIcon
                onClick={() => setShowPassword(!showPassword)}
              ></EyeOffIcon>
            )
          }
          iconPosition="right"
          type={showPassword ? 'text' : 'password'}
        />
      </ContainerInput>

      <ContainerLink>
        <ExternalLink
          href={process.env.REACT_APP_LOGIN_URL?.replace(
            '/auth',
            '/forgot-password'
          )}
          target="_blank"
        >
          Esqueci minha senha
        </ExternalLink>
      </ContainerLink>
    </form>
  );

  return (
    <div style={{ zIndex: 9999, position: 'absolute' }}>
      <Modal
        buttons={{
          left: {
            children: 'Encerrar a sessão',
            name: 'end-session',
            onClick: handleFinishSession,
            type: 'button',
            variant: 'secondary',
            loading: showLoading
          },
          right: {
            children: 'Continuar a sessão',
            name: 'continue-session',
            onClick: handleSubmit,
            type: 'button',
            variant: 'primary',
            loading: showLoading,
            disabled:
              !email.length ||
              !password.length ||
              errors.email ||
              errors.password
          }
        }}
        modalSize="sm"
        title="Deseja continuar a sessão?"
      >
        <span>
          Por segurança, sua sessão foi interrompida. Digite seus dados de
          acesso para continuar acessando:
        </span>
        {loginForm}
      </Modal>
    </div>
  );
};

export default ModalLogin;
